import { FormEvent, useEffect, useState } from "react";
import { Edit, Plus, RefreshCw, Search, ShieldCheck, Trash2, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import {
  AdminPermissionCatalogItem,
  AdminUser,
  AdminUserPayload,
  AdminUserRoleOption,
  PaginationMeta,
  createAdminUser,
  deleteAdminUser,
  getCurrentAdmin,
  listAdminUsers,
  logout,
  updateAdminUser,
} from "./api";
import AdminPasswordInput from "./components/AdminPasswordInput";
import { useConfirmDialog } from "./components/ConfirmDialog";
import Pagination from "./components/Pagination";
import { AdminSwitchCard } from "./components/AdminChoiceControl";

type UserFormState = {
  Username: string;
  DisplayName: string;
  Role: string;
  IsActive: boolean;
  Password: string;
  IsSuperAdmin: boolean;
  Permissions: string[];
};

const emptyForm: UserFormState = {
  Username: "",
  DisplayName: "",
  Role: "Admin",
  IsActive: true,
  Password: "",
  IsSuperAdmin: false,
  Permissions: [],
};

const defaultPagination: PaginationMeta = {
  Page: 1,
  PerPage: 20,
  Total: 0,
  TotalPages: 1,
};

const permissionGroups = [
  {
    title: "預約與客服",
    keys: ["reservations", "product-trial-reservations", "booking-availability", "contact-messages", "customer-service"],
  },
  {
    title: "門市管理",
    keys: ["store-overview", "store-customers", "store-users", "store-sales-orders", "store-order-statuses", "store-service-records", "store-follow-ups", "store-reports", "store-payment-reconciliation", "store-announcements", "store-activity-logs"],
  },
  {
    title: "商品與訂單",
    keys: ["products", "orders", "members", "refunds", "finance-reports", "payment-transactions", "invoices"],
  },
  {
    title: "內容管理",
    keys: ["knowledge-articles", "faq-items", "news-items", "team-members", "testimonials", "operation-locations", "newsletter-subscribers", "newsletter-campaigns"],
  },
  {
    title: "網站與系統",
    keys: ["home-sections", "company-profile", "static-pages", "shipping-methods", "logistics-setting", "shipping-rules", "shipping-coupons", "payment-methods", "product-categories", "knowledge-article-categories", "faq-categories", "news-categories", "admin-users", "admin-menu"],
  },
];

const erpRoleDefaultPermissions: Record<string, string[]> = {
  ErpManager: ["erp-dashboard", "erp-inventory", "erp-stock-units", "erp-stock-movements", "erp-inventory-counts", "erp-stock-adjustments", "erp-stock-transfers", "erp-purchase-orders", "erp-purchase-receipts", "erp-rma-orders", "erp-payables", "erp-quality-inspections", "erp-outbound-orders", "erp-warehouses", "erp-suppliers", "erp-accounting-periods", "erp-reports", "erp-tasks", "erp-import-batches", "erp-audit-logs", "erp-attachments"],
  ErpWarehouse: ["erp-dashboard", "erp-inventory", "erp-stock-units", "erp-stock-movements", "erp-inventory-counts", "erp-stock-adjustments", "erp-stock-transfers", "erp-purchase-receipts", "erp-quality-inspections", "erp-outbound-orders", "erp-warehouses"],
  ErpPurchase: ["erp-dashboard", "erp-inventory", "erp-stock-units", "erp-stock-movements", "erp-purchase-orders", "erp-purchase-receipts", "erp-rma-orders", "erp-suppliers", "erp-import-batches", "erp-attachments"],
  ErpFinance: ["erp-dashboard", "erp-inventory", "erp-stock-units", "erp-stock-movements", "erp-payables", "erp-accounting-periods", "erp-reports", "erp-tasks", "erp-audit-logs", "erp-attachments"],
  ErpViewer: ["erp-dashboard", "erp-inventory", "erp-stock-units", "erp-stock-movements"],
};

const erpPermissionGroups = [
  {
    title: "ERP 工作台",
    keys: ["erp-dashboard"],
  },
  {
    title: "庫存管理",
    keys: ["erp-inventory", "erp-stock-units", "erp-stock-movements"],
  },
  {
    title: "盤點與調撥",
    keys: ["erp-inventory-counts", "erp-stock-adjustments", "erp-stock-transfers"],
  },
  {
    title: "採購與驗收",
    keys: ["erp-purchase-orders", "erp-purchase-receipts", "erp-rma-orders"],
  },
  {
    title: "工程與出庫",
    keys: ["erp-quality-inspections", "erp-outbound-orders"],
  },
  {
    title: "財務與報表",
    keys: ["erp-payables", "erp-accounting-periods", "erp-reports", "erp-tasks", "erp-audit-logs"],
  },
  {
    title: "基礎與治理",
    keys: ["erp-warehouses", "erp-suppliers", "erp-import-batches", "erp-attachments", "erp-attachments-confidential"],
  },
];

function groupedPermissionCatalog(catalog: AdminPermissionCatalogItem[]) {
  const adminPortalCatalog = catalog.filter((item) => !item.Key.startsWith("erp-"));
  const catalogMap = new Map(adminPortalCatalog.map((item) => [item.Key, item]));
  const used = new Set<string>();
  const groups = permissionGroups
    .map((group) => {
      const items = group.keys.map((key) => catalogMap.get(key)).filter((item): item is AdminPermissionCatalogItem => Boolean(item));
      items.forEach((item) => used.add(item.Key));
      return { title: group.title, items };
    })
    .filter((group) => group.items.length > 0);
  const others = adminPortalCatalog.filter((item) => !used.has(item.Key));
  return others.length > 0 ? [...groups, { title: "其他", items: others }] : groups;
}

function groupedErpPermissionCatalog(catalog: AdminPermissionCatalogItem[]) {
  const catalogMap = new Map(catalog.filter((item) => item.Key.startsWith("erp-")).map((item) => [item.Key, item]));
  const used = new Set<string>();
  const groups = erpPermissionGroups
    .map((group) => {
      const items = group.keys.map((key) => catalogMap.get(key)).filter((item): item is AdminPermissionCatalogItem => Boolean(item));
      items.forEach((item) => used.add(item.Key));
      return { title: group.title, items };
    })
    .filter((group) => group.items.length > 0);
  const others = Array.from(catalogMap.values()).filter((item) => !used.has(item.Key));
  return others.length > 0 ? [...groups, { title: "其他 ERP 權限", items: others }] : groups;
}

function isErpRole(role: string) {
  return role.startsWith("Erp");
}

export default function AdminUsers() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [counts, setCounts] = useState({ Active: 0, Inactive: 0 });
  const [roleCounts, setRoleCounts] = useState<Record<string, number>>({});
  const [pagination, setPagination] = useState<PaginationMeta>(defaultPagination);
  const [status, setStatus] = useState("");
  const [role, setRole] = useState("");
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pageMessage, setPageMessage] = useState("");
  const [formMessage, setFormMessage] = useState("");
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<UserFormState>(emptyForm);
  const [permissionCatalog, setPermissionCatalog] = useState<AdminPermissionCatalogItem[]>([]);
  const [roleOptions, setRoleOptions] = useState<AdminUserRoleOption[]>([{ Key: "Admin", Label: "總管理者" }]);
  const [isActingSuperAdmin, setIsActingSuperAdmin] = useState(false);
  const { confirm, ConfirmDialog } = useConfirmDialog();

  async function load(filters = { Status: status, Role: role, Keyword: keyword, Page: pagination.Page, PerPage: pagination.PerPage }) {
    setLoading(true);
    setPageMessage("");

    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
      const data = await listAdminUsers(filters);
      setUsers(data.Users);
      setCounts(data.Counts || { Active: 0, Inactive: 0 });
      setRoleCounts(data.RoleCounts || {});
      setPermissionCatalog(data.PermissionCatalog || []);
      setRoleOptions(data.RoleOptions || [{ Key: "Admin", Label: "總管理者" }]);
      setIsActingSuperAdmin(data.IsActingSuperAdmin || false);
      setPagination(data.Pagination || defaultPagination);
    } catch (error) {
      if (error instanceof Error) setPageMessage(error.message);
      navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load({ Status: "", Role: "", Keyword: "", Page: 1, PerPage: pagination.PerPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function openCreateForm() {
    setEditingUser(null);
    setForm(emptyForm);
    setPageMessage("");
    setFormMessage("");
    setFormOpen(true);
  }

  function openEditForm(user: AdminUser) {
    const role = user.Role || "Admin";
    const userPermissions = user.Permissions || [];
    const permissions = isErpRole(role)
      ? userPermissions.filter((key) => key.startsWith("erp-"))
      : userPermissions.filter((key) => !key.startsWith("erp-"));
    setEditingUser(user);
    setForm({
      Username: user.Username,
      DisplayName: user.DisplayName,
      Role: role,
      IsActive: user.IsActive !== false,
      Password: "",
      IsSuperAdmin: isErpRole(role) ? false : user.IsSuperAdmin === true,
      Permissions: permissions,
    });
    setPageMessage("");
    setFormMessage("");
    setFormOpen(true);
  }

  function togglePermission(key: string) {
    setForm((current) => ({
      ...current,
      Permissions: current.Permissions.includes(key)
        ? current.Permissions.filter((item) => item !== key)
        : [...current.Permissions, key],
    }));
  }

  function handleRoleChange(role: string) {
    setForm((current) => {
      const defaults = erpRoleDefaultPermissions[role] || [];
      if (defaults.length === 0) {
        return {
          ...current,
          Role: role,
          IsSuperAdmin: current.IsSuperAdmin,
          Permissions: current.Permissions.filter((key) => !key.startsWith("erp-")),
        };
      }
      return {
        ...current,
        Role: role,
        IsSuperAdmin: false,
        Permissions: Array.from(new Set(defaults)),
      };
    });
  }

  function validateForm() {
    if (!editingUser && !/^[A-Za-z0-9_.@-]{4,80}$/.test(form.Username.trim())) {
      return "帳號需為 4-80 字元，可使用英文、數字、底線、點、@ 或連字號。";
    }
    if (!form.DisplayName.trim()) return "請輸入顯示名稱。";
    if (!editingUser && form.Password.length < 12) return "新增帳號時密碼至少需要 12 個字元。";
    if (editingUser && form.Password && form.Password.length < 12) return "重設密碼至少需要 12 個字元。";
    return "";
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const validationMessage = validateForm();
    if (validationMessage) {
      setFormMessage(validationMessage);
      return;
    }

    const payload: AdminUserPayload = {
      Username: form.Username.trim(),
      DisplayName: form.DisplayName.trim(),
      Role: form.Role,
      IsActive: form.IsActive,
      Password: form.Password || undefined,
    };
    // 只有目前登入的帳號是超級管理者，才把這兩個欄位放進請求裡；一般帳號就算畫面上看不到這些欄位，
    // 也不應該送出去（後端本來就會拒絕，這裡單純避免送出無意義的欄位）。
    if (isActingSuperAdmin) {
      const isErpAccount = isErpRole(form.Role);
      payload.IsSuperAdmin = isErpAccount ? false : form.IsSuperAdmin;
      payload.Permissions = isErpAccount
        ? form.Permissions.filter((key) => key.startsWith("erp-"))
        : form.Permissions.filter((key) => !key.startsWith("erp-"));
    }

    setSaving(true);
    setFormMessage("");
    try {
      if (editingUser) {
        await updateAdminUser(editingUser.AdminUserId, payload);
      } else {
        await createAdminUser(payload);
      }
      setFormOpen(false);
      await load({ Status: status, Role: role, Keyword: keyword.trim(), Page: pagination.Page, PerPage: pagination.PerPage });
    } catch (error) {
      setFormMessage(error instanceof Error ? error.message : "儲存失敗，請稍後再試。");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(user: AdminUser) {
    if (currentUser?.AdminUserId === user.AdminUserId) {
      setPageMessage("不可刪除目前登入中的帳號。");
      return;
    }

    const confirmed = await confirm({
      title: "刪除管理者帳號？",
      description: (
        <>
          你即將刪除管理者帳號 <strong className="font-bold text-[#203d32]">{user.Username}</strong>。
          <br />
          此操作無法復原，刪除後該帳號將無法登入後台。
        </>
      ),
      confirmText: "刪除帳號",
      cancelText: "保留帳號",
      tone: "danger",
    });
    if (!confirmed) return;

    setPageMessage("");
    try {
      await deleteAdminUser(user.AdminUserId);
      await load({ Status: status, Role: role, Keyword: keyword.trim(), Page: pagination.Page, PerPage: pagination.PerPage });
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "刪除失敗，請稍後再試。");
    }
  }

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    load({ Status: status, Role: role, Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage });
  }

  function resetFilters() {
    setStatus("");
    setRole("");
    setKeyword("");
    load({ Status: "", Role: "", Keyword: "", Page: 1, PerPage: pagination.PerPage });
  }

  const adminPortalPermissionKeys = permissionCatalog.filter((item) => !item.Key.startsWith("erp-")).map((item) => item.Key);
  const erpPermissionKeys = permissionCatalog.filter((item) => item.Key.startsWith("erp-")).map((item) => item.Key);
  const isErpAccount = isErpRole(form.Role);

  return (
    <AdminLayout sectionLabel="系統管理" title="管理者帳號管理" user={currentUser} onLogout={handleLogout}>
      <form className="mb-5 grid gap-3 rounded-md border border-[#dfe6e1] bg-white p-3 md:flex md:flex-wrap md:items-end md:p-4" onSubmit={handleSearch}>
        <div className="grid min-w-0 gap-1 md:min-w-[120px]">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="admin-user-status">
            狀態
          </label>
          <select
            className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 outline-none transition focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
            id="admin-user-status"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="">全部</option>
            <option value="Active">啟用中</option>
            <option value="Inactive">已停用</option>
          </select>
        </div>

        <div className="grid min-w-0 gap-1 md:min-w-[190px]">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="admin-user-role">
            角色
          </label>
          <select
            className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 outline-none transition focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
            id="admin-user-role"
            value={role}
            onChange={(event) => setRole(event.target.value)}
          >
            <option value="">全部角色</option>
            {roleOptions.map((option) => (
              <option key={option.Key} value={option.Key}>
                {option.Label}
              </option>
            ))}
          </select>
        </div>

        <div className="grid min-w-0 gap-1 md:min-w-[260px] md:flex-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="admin-user-keyword">
            關鍵字
          </label>
          <input
            className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none transition focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
            id="admin-user-keyword"
            placeholder="帳號、顯示名稱"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
          />
        </div>

        <button className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32] md:w-auto">
          <Search size={18} />
          查詢
        </button>

        <div className="grid w-full grid-cols-1 gap-2 min-[520px]:grid-cols-3 md:w-auto md:flex md:gap-3">
          <button
            className="inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-[#cfd8d2] bg-white px-3 font-bold text-[#315746] transition hover:bg-[#f0f5f2] md:px-5"
            onClick={() => load({ Status: status, Role: role, Keyword: keyword.trim(), Page: pagination.Page, PerPage: pagination.PerPage })}
            type="button"
          >
            <RefreshCw size={18} />
            <span className="hidden min-[380px]:inline">重新整理</span>
            <span className="min-[380px]:hidden">整理</span>
          </button>
          <button
            className="inline-flex h-11 items-center justify-center whitespace-nowrap rounded-md border border-[#cfd8d2] bg-white px-3 font-bold text-[#315746] transition hover:bg-[#f0f5f2] md:px-5"
            onClick={resetFilters}
            type="button"
          >
            重設篩選
          </button>
          <button
            className="inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-[#315746] bg-[#315746] px-3 font-bold text-white transition hover:bg-[#203d32] md:px-5"
            onClick={openCreateForm}
            type="button"
          >
            <Plus size={20} />
            新增管理者
          </button>
        </div>
      </form>

      <div className="mb-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3">
          <p className="text-sm font-bold text-[#7a867f]">啟用中</p>
          <p className="mt-1 text-2xl font-black text-[#0b3328]">{counts.Active}</p>
        </div>
        <div className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3">
          <p className="text-sm font-bold text-[#7a867f]">已停用</p>
          <p className="mt-1 text-2xl font-black text-[#0b3328]">{counts.Inactive}</p>
        </div>
        <div className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3 sm:col-span-2">
          <p className="text-sm font-bold text-[#7a867f]">角色分布</p>
          <div className="mt-2 flex flex-wrap gap-2 text-sm font-bold text-[#315746]">
            {roleOptions.map((option) => (
              <span className="rounded-full bg-[#eef5f1] px-3 py-1" key={option.Key}>
                {option.Label} {roleCounts[option.Key] || 0}
              </span>
            ))}
          </div>
        </div>
      </div>

      {pageMessage && (
        <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">
          {pageMessage}
        </div>
      )}

      <div className="rounded-md border border-[#dfe6e1] bg-white">
        <div className="max-h-[62vh] overflow-auto">
          <table className="w-full min-w-[980px] border-collapse text-left">
            <thead className="sticky top-0 z-10 bg-[#f0f3f1] text-sm text-[#59665f] shadow-[inset_0_-1px_0_#dfe6e1]">
              <tr>
                <th className="px-4 py-3">帳號</th>
                <th className="px-4 py-3">顯示名稱</th>
                <th className="px-4 py-3">角色</th>
                <th className="px-4 py-3">狀態</th>
                <th className="px-4 py-3">最後登入</th>
                <th className="px-4 py-3">建立時間</th>
                <th className="px-4 py-3">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="px-4 py-10 text-center text-[#59665f]" colSpan={7}>
                    載入中...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td className="px-4 py-10 text-center text-[#59665f]" colSpan={7}>
                    目前沒有符合條件的管理者帳號。
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr className="border-t border-[#edf0ee]" key={user.AdminUserId}>
                    <td className="px-4 py-4 align-top font-bold">{user.Username}</td>
                    <td className="px-4 py-4 align-top">{user.DisplayName}</td>
                    <td className="px-4 py-4 align-top">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {user.RoleLabel || user.Role}
                        {user.IsSuperAdmin && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-[#fdf3e3] px-2 py-0.5 text-[11px] font-bold text-[#8a6a2f]">
                            <ShieldCheck size={12} />
                            超級管理者
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 align-top">
                      <span
                        className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${
                          user.IsActive === false ? "bg-[#eeeeee] text-[#59635d]" : "bg-[#e9f4ec] text-[#245438]"
                        }`}
                      >
                        {user.IsActive === false ? "已停用" : "啟用中"}
                      </span>
                    </td>
                    <td className="px-4 py-4 align-top text-sm text-[#59665f]">{user.LastLoginAt || "-"}</td>
                    <td className="px-4 py-4 align-top text-sm text-[#59665f]">{user.CreatedAt || "-"}</td>
                    <td className="px-4 py-4 align-top">
                      <div className="flex flex-wrap gap-2">
                        <button
                          className="inline-flex h-9 items-center gap-1 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] transition hover:bg-[#f0f5f2]"
                          onClick={() => openEditForm(user)}
                          type="button"
                        >
                          <Edit size={16} />
                          編輯
                        </button>
                        <button
                          className="inline-flex h-9 items-center gap-1 rounded-md border border-[#f0c7c4] bg-white px-3 text-sm font-bold text-[#9a2d2d] transition hover:bg-[#fdebea]"
                          onClick={() => handleDelete(user)}
                          type="button"
                        >
                          <Trash2 size={16} />
                          刪除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          disabled={loading}
          pagination={pagination}
          onChange={(page) => load({ Status: status, Role: role, Keyword: keyword.trim(), Page: page, PerPage: pagination.PerPage })}
        />
      </div>

      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-[#10251c]/35 px-0 py-0 sm:items-center sm:px-4 sm:py-6">
          <form
            className="flex max-h-[94vh] w-full max-w-[1440px] flex-col overflow-hidden rounded-t-lg border border-[#dfe6e1] bg-white shadow-[0_22px_70px_rgba(32,61,50,.18)] sm:w-[calc(100vw-32px)] sm:rounded-md xl:max-h-[90vh]"
            onSubmit={handleSubmit}
          >
            <div className="flex shrink-0 items-center justify-between border-b border-[#edf0ee] px-5 py-4">
              <div>
                <p className="text-xs font-bold tracking-[.16em] text-[#7b877f]">ADMIN USER</p>
                <h2 className="text-xl font-bold text-[#203d32]">{editingUser ? "編輯管理者" : "新增管理者"}</h2>
              </div>
              <button
                aria-label="關閉"
                className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-[#d8e0db] text-[#315746] transition hover:bg-[#f0f5f2]"
                onClick={() => setFormOpen(false)}
                type="button"
              >
                <X size={18} />
              </button>
            </div>

            <div className="grid gap-4 overflow-y-auto px-5 py-5">
              {formMessage && (
                <div className="rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium leading-6 text-[#8a1f1f]">
                  {formMessage}
                </div>
              )}

              <div>
                <label className="mb-2 block text-sm font-bold text-[#315746]" htmlFor="admin-username">
                  帳號
                </label>
                <input
                  className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none transition disabled:bg-[#f4f6f5] focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                  disabled={Boolean(editingUser)}
                  id="admin-username"
                  maxLength={80}
                  value={form.Username}
                  onChange={(event) => setForm((current) => ({ ...current, Username: event.target.value }))}
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-bold text-[#315746]" htmlFor="admin-display-name">
                  顯示名稱
                </label>
                <input
                  className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none transition focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                  id="admin-display-name"
                  maxLength={80}
                  value={form.DisplayName}
                  onChange={(event) => setForm((current) => ({ ...current, DisplayName: event.target.value }))}
                />
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <div className="rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-4">
                  <label className="mb-2 block text-sm font-bold text-[#315746]" htmlFor="admin-role">
                    角色
                  </label>
                  <select
                    className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 outline-none transition focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                    id="admin-role"
                    value={form.Role}
                    onChange={(event) => handleRoleChange(event.target.value)}
                  >
                    {roleOptions.map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}
                  </select>
                  <p className="mt-2 text-xs leading-5 text-[#7b877f]">
                    選「總管理者」時只設定總管理端權限；選 ERP 角色時只設定 ERP Portal 權限，兩邊不混用。
                  </p>
                </div>

                <AdminSwitchCard
                  checked={form.IsActive}
                  description={form.IsActive ? "啟用後可登入後台並使用管理功能。" : "停用後此帳號將無法登入後台。"}
                  label="帳號狀態"
                  onChange={(IsActive) => setForm((current) => ({ ...current, IsActive }))}
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-bold text-[#315746]" htmlFor="admin-password">
                  {editingUser ? "重設密碼（選填）" : "初始密碼"}
                </label>
                <AdminPasswordInput
                  autoComplete="new-password"
                  className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none transition focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                  id="admin-password"
                  value={form.Password}
                  onChange={(value) => setForm((current) => ({ ...current, Password: value }))}
                />
                <p className="mt-2 text-xs text-[#7b877f]">密碼至少 12 個字元。編輯時留空代表不變更密碼。</p>
              </div>

              {isActingSuperAdmin && (
                <div className="rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-4">
                  {!isErpAccount && (
                    <AdminSwitchCard
                      checked={form.IsSuperAdmin}
                      description="超級總管理者只適用總管理端帳號；ERP 帳號需以 ERP 角色與 ERP 權限控管。"
                      label="超級總管理者"
                      onChange={(IsSuperAdmin) => setForm((current) => ({ ...current, IsSuperAdmin }))}
                    />
                  )}

                  {!isErpAccount && !form.IsSuperAdmin && (
                    <div className="mt-4 border-t border-[#edf0ee] pt-4">
                      <div className="mb-2 flex items-center justify-between">
                        <p className="text-sm font-bold text-[#315746]">頁面權限</p>
                        <div className="flex gap-2 text-xs font-bold text-[#315746]">
                          <button
                            className="hover:underline"
                            onClick={() => setForm((current) => ({ ...current, Permissions: Array.from(new Set([...current.Permissions.filter((key) => key.startsWith("erp-")), ...adminPortalPermissionKeys])) }))}
                            type="button"
                          >
                            全選
                          </button>
                          <span className="text-[#c9d2cc]">|</span>
                          <button
                            className="hover:underline"
                            onClick={() => setForm((current) => ({ ...current, Permissions: current.Permissions.filter((key) => key.startsWith("erp-")) }))}
                            type="button"
                          >
                            取消全選
                          </button>
                        </div>
                      </div>
                      <p className="mb-3 text-xs leading-5 text-[#7b877f]">勾選這個帳號可以使用的後台頁面，未勾選的頁面在選單裡會被隱藏，直接輸入網址也無法使用。</p>
                      <div className="grid gap-4">
                        {groupedPermissionCatalog(permissionCatalog).map((group) => (
                          <section className="rounded-md border border-[#e3e9e5] bg-white p-3" key={group.title}>
                            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                              <p className="text-sm font-bold text-[#203d32]">{group.title}</p>
                              <div className="flex items-center gap-2 text-xs font-bold text-[#315746]">
                                <button
                                  className="hover:underline"
                                  onClick={() => setForm((current) => ({ ...current, Permissions: Array.from(new Set([...current.Permissions, ...group.items.map((item) => item.Key)])) }))}
                                  type="button"
                                >
                                  全選此分類
                                </button>
                                <span className="text-[#c9d2cc]">|</span>
                                <button
                                  className="hover:underline"
                                  onClick={() => setForm((current) => ({ ...current, Permissions: current.Permissions.filter((key) => !group.items.some((item) => item.Key === key)) }))}
                                  type="button"
                                >
                                  清除此分類
                                </button>
                              </div>
                            </div>
                            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                              {group.items.map((item) => (
                                <label
                                  className="flex min-h-11 cursor-pointer items-center gap-2 rounded-md border border-[#e3e9e5] bg-[#fbfcfb] px-3 py-2 text-sm text-[#315746] transition hover:border-[#b8c8bf]"
                                  key={item.Key}
                                >
                                  <input
                                    checked={form.Permissions.includes(item.Key)}
                                    className="h-4 w-4 accent-[#315746]"
                                    onChange={() => togglePermission(item.Key)}
                                    type="checkbox"
                                  />
                                  <span className="min-w-0 whitespace-normal break-words leading-5">{item.Label}</span>
                                </label>
                              ))}
                            </div>
                          </section>
                        ))}
                      </div>
                    </div>
                  )}

                  {isErpAccount && (
                    <div className="mt-4 border-t border-[#edf0ee] pt-4">
                          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                            <div>
                              <p className="text-sm font-bold text-[#315746]">ERP Portal 權限</p>
                              <p className="mt-1 text-xs leading-5 text-[#7b877f]">此區只影響 `/erp-login` 與 ERP Portal 內功能，不會顯示在總管理端左側選單。</p>
                            </div>
                            <div className="flex gap-2 text-xs font-bold text-[#315746]">
                              <button
                                className="hover:underline"
                                onClick={() => setForm((current) => ({ ...current, Permissions: Array.from(new Set([...current.Permissions, ...erpPermissionKeys])) }))}
                                type="button"
                              >
                                全選 ERP
                              </button>
                              <span className="text-[#c9d2cc]">|</span>
                              <button
                                className="hover:underline"
                                onClick={() => setForm((current) => ({ ...current, Permissions: current.Permissions.filter((key) => !key.startsWith("erp-")) }))}
                                type="button"
                              >
                                清除 ERP
                              </button>
                            </div>
                          </div>
                          <div className="grid gap-4">
                            {groupedErpPermissionCatalog(permissionCatalog).map((group) => (
                              <section className="rounded-md border border-[#d8e5dd] bg-[#f8fbf8] p-3" key={group.title}>
                                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                                  <p className="text-sm font-bold text-[#203d32]">{group.title}</p>
                                  <div className="flex items-center gap-2 text-xs font-bold text-[#315746]">
                                    <button
                                      className="hover:underline"
                                      onClick={() => setForm((current) => ({ ...current, Permissions: Array.from(new Set([...current.Permissions, ...group.items.map((item) => item.Key)])) }))}
                                      type="button"
                                    >
                                      全選此分類
                                    </button>
                                    <span className="text-[#c9d2cc]">|</span>
                                    <button
                                      className="hover:underline"
                                      onClick={() => setForm((current) => ({ ...current, Permissions: current.Permissions.filter((key) => !group.items.some((item) => item.Key === key)) }))}
                                      type="button"
                                    >
                                      清除此分類
                                    </button>
                                  </div>
                                </div>
                                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                                  {group.items.map((item) => (
                                    <label
                                      className="flex min-h-11 cursor-pointer items-center gap-2 rounded-md border border-[#dfe8e2] bg-white px-3 py-2 text-sm text-[#315746] transition hover:border-[#b8c8bf]"
                                      key={item.Key}
                                    >
                                      <input
                                        checked={form.Permissions.includes(item.Key)}
                                        className="h-4 w-4 accent-[#315746]"
                                        onChange={() => togglePermission(item.Key)}
                                        type="checkbox"
                                      />
                                      <span className="min-w-0 whitespace-normal break-words leading-5">{item.Label}</span>
                                    </label>
                                  ))}
                                </div>
                              </section>
                            ))}
                          </div>
                        </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex shrink-0 flex-col-reverse gap-3 border-t border-[#edf0ee] px-5 py-4 sm:flex-row sm:justify-end">
              <button
                className="h-10 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] transition hover:bg-[#f0f5f2]"
                onClick={() => setFormOpen(false)}
                type="button"
              >
                取消
              </button>
              <button
                className="h-10 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32] disabled:cursor-not-allowed disabled:opacity-60"
                disabled={saving}
                type="submit"
              >
                {saving ? "儲存中" : "儲存"}
              </button>
            </div>
          </form>
        </div>
      )}
      <ConfirmDialog />
    </AdminLayout>
  );
}
